import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styles from './EditorRightSidebar.module.scss';
import cx from 'classnames';
import EditorSidebarActions from '../EditorSidebarActions/EditorSidebarActions';
import { useBackgroundMusicContext } from '../../context/BackgroundMusicContext';
import { useTextPanelContext } from '../../context/TextPanelContext';
import NameEditComponent from '../NameEditComponent/NameEditComponent';
import TextComponent from '../GeneratedSceneItem/TextComponent';
import CommentsTab from '../CommentsTab/CommentsTab';
import { useUserContext } from '../../context/UserContext';
import { CustomButton } from '../Button/Button';
import { useVideoSpeedUpContext } from '../../context/VideoSpeedUpContext';
import { useIntroOutroContext } from '../../context/IntroOutroContext';
import { useLogoContext } from '../../context/LogoContext';
import { useZoomContext } from '../../context/ZoomContext';
import UsersListPreview from '../UsersListPreview/UsersListPreview';
import RightSidebarContainer from '../../features/Editor/components/RightSidebarContainer/RightSidebarContainer';
import { SIDEBAR_STATE_LIST } from '../../features/Editor/const';
import useStoryComments from '../../features/Editor/hooks/useStoryComments';

const commentsType = 3;

const EditorRightSidebar = ({
  storyId,
  video,
  logo,
  isDraft,
  videoByParts,
  currentVideoPart,
  sceneIndex,
  activeScene,
  rename,
  handleRename,
  handleSaveClip,
  saving,
  isSceneProcessing,
  cancelEditing,
  handleUndo,
  handleRedo,
  controlsDisabled,
  trimmingMode,
  handleTrimVideo,
  init,
  hasChanged,
  handleStartTrimming,
  switchingTrimming,
  isTransitionExists,
  isTransitionAllowed,
  activeTransitionRightMedia,
  onTransitionChanged,
  currentTransitionType,
  handleSaveTextPanel,
  handleIntroOutro,
  onBackgroundMusicChange,
  handleSpeedUpChange,
  isVideoHasHls,
  handleSplit,
  toggleDisableNonClipCards,
  handleUpdateScene,
  userPermissions,
  handleLogoChange,
  handleZoomPointsChange,
  assignments,
  miniRightSidebar,
  setMiniRightSidebar,
  addTransitionToScene,
  isRecording,
  handleIsEditing,
  handleEditStoryboard,
  handleCompleteAssignment,
  isCurrentAssignedScenesCompleted,
  isCurrentAssignedScenesRecorded,
  collaboratingUsers,
  onEnhanceScene,
  onRemoveEnhanceScene,
  onShowAdvancedEditorDialog,
}) => {
  const { t } = useTranslation();

  const { user, isAdvancedEditorButtonEnabled } = useUserContext();

  const currentClipData = useMemo(() => {
    const clipIndex = Number(currentVideoPart);
    if (videoByParts?.scenes && clipIndex >= 0) {
      return videoByParts?.scenes[clipIndex];
    } else {
      return null;
    }
  }, [videoByParts, currentVideoPart]);


  const canSeeEditOptions = useMemo(() => (userPermissions?.isOwner || userPermissions.isFolderEditor) || userPermissions?.updateStory || userPermissions?.allowedScenes?.length > 0, [userPermissions, assignments]);
  const [sidebarState, setSidebarState] = useState(canSeeEditOptions ? SIDEBAR_STATE_LIST.EDIT.TYPE : SIDEBAR_STATE_LIST.STORYBOARD.TYPE);

  const { isCommentsLoading, comments, newComment, commentInit, setNewComment, loadComments } = useStoryComments({ storyId, commentsType, load: sidebarState === SIDEBAR_STATE_LIST.CHAT.TYPE });

  const isSceneAllowed = useMemo(() => userPermissions?.updateStory || userPermissions?.allowedScenes.find(id => id === activeScene?.id), [userPermissions, activeScene?.id]);

  const [isTextEdit, setIsTextEdit] = useState(false);
  const [editSceneId, setEditSceneId] = useState(null);

  const { isBackgroundMusicOpened, initBackgroundMusic } = useBackgroundMusicContext();
  const { isTextPanelEditor } = useTextPanelContext();
  const { isIntroOutroOpened } = useIntroOutroContext();
  const { isVideoSpeedUpOpened } = useVideoSpeedUpContext();
  const { isLogoEditor } = useLogoContext();
  const { isZoomOpened } = useZoomContext();

  const userListRef = useRef(null);

  const toggleMiniSidebar = useCallback(state => {
    if (!miniRightSidebar) {
      switchBetweenTabs(SIDEBAR_STATE_LIST.EDIT.TYPE);
    }
    if (typeof state === 'boolean') setMiniRightSidebar(state);
    else setMiniRightSidebar(prevState => !prevState);
  }, []);

  const editorOptionShown = useMemo(() => trimmingMode ||
    switchingTrimming ||
    isTextPanelEditor ||
    isBackgroundMusicOpened ||
    rename.flag ||
    isIntroOutroOpened ||
    isVideoSpeedUpOpened ||
    isLogoEditor ||
    isZoomOpened, [
    trimmingMode,
    switchingTrimming,
    isTextPanelEditor,
    isBackgroundMusicOpened,
    rename,
    isIntroOutroOpened,
    isVideoSpeedUpOpened,
    isLogoEditor,
    isZoomOpened,
  ]);

  useEffect(() => {
    handleIsEditing && handleIsEditing(editorOptionShown);
  }, [editorOptionShown, handleIsEditing])

  useEffect(() => {
    if (rename.flag && sidebarState != SIDEBAR_STATE_LIST.EDIT.TYPE) {
      switchBetweenTabs(SIDEBAR_STATE_LIST.EDIT.TYPE);
    }
  }, [rename]);

  const sidebarOptions = useMemo(
    () => Object.values(SIDEBAR_STATE_LIST).filter(({ TYPE }) => {
      switch (TYPE) {
        case SIDEBAR_STATE_LIST.CHAT.TYPE:
          return (userPermissions?.isOwner || userPermissions?.isFolderEditor) ? assignments.length > 0 : userPermissions?.readStory || userPermissions?.updateStory || userPermissions?.allowedScenes?.length > 0;
        case SIDEBAR_STATE_LIST.EDIT.TYPE:
          return canSeeEditOptions;
        default:
          return true;
      }
    }),
    [assignments, userPermissions?.isOwner, canSeeEditOptions]
  );
  const history = useHistory();

  const handleClose = useCallback(() => {
    history.push(`/eg/video/${storyId}`);
  }, [video, userPermissions, videoByParts, isDraft]);

  const handleNameChange = useCallback(name => {
    if (name.trim().length > 0) {
      handleUpdateScene(editSceneId, { name, nameChanged: true });
      setEditSceneId(null);
    }
  }, [editSceneId]);

  const handleNarrativeChange = useCallback(async (narrative) => {
    handleUpdateScene(editSceneId, { narrative, narrativeChanged: true });
    setEditSceneId(null);
  }, [editSceneId]);

  const handleScriptChange = useCallback(async (script) => {
    handleUpdateScene(editSceneId, { script, scriptChanged: true });
    setEditSceneId(null);
  }, [editSceneId]);

  const switchBetweenTabs = type => {
    switch (type) {
      case SIDEBAR_STATE_LIST.STORYBOARD.TYPE:
        toggleDisableNonClipCards(true);
        setTimeout(() => {
          setSidebarState(type);
        }, 50);
        break;
      case SIDEBAR_STATE_LIST.EDIT.TYPE:
        toggleDisableNonClipCards(false);
      default:
        setSidebarState(type);
    }
  };

  const changeTextEdit = value => {
    setIsTextEdit(value);
    setEditSceneId(value ? currentClipData?.id : null);
  };

  useEffect(() => {
    if (rename.flag) {
      setMiniRightSidebar(false);
    }
  }, [rename]);

  useEffect(() => {
    if (videoByParts?.scenes?.length > 0) {
      let isSameTrack = null;
      let isSameVolume = videoByParts.scenes.every(scene => scene.videoVolume == videoByParts.scenes[0]?.videoVolume);
      if (videoByParts.scenes.some(scene => scene.music !== null)) {
        isSameTrack = videoByParts.scenes.every(scene => scene.music?.id === videoByParts.scenes[0]?.music?.id);
        isSameVolume = videoByParts.scenes.every(scene => scene.music?.volume === videoByParts?.scenes[0]?.music?.volume && scene.videoVolume == videoByParts.scenes[0].videoVolume);
      }
      initBackgroundMusic(isSameTrack, isSameVolume);
    }
  }, [videoByParts])

  return (
    <RightSidebarContainer
      classNames={cx({ [styles.midSize]: isIntroOutroOpened || trimmingMode })}
      mini={miniRightSidebar}
      toggleMiniSidebar={toggleMiniSidebar}
      miniClassNames={styles.miniSidebar}
      showHeader={editorOptionShown}
      handleClose={handleClose}
      defaultTitle={t('clip_organizer.sidebar_default_title')}
      tabs={{
        options: sidebarOptions,
        state: sidebarState,
        onChangeTab: switchBetweenTabs,
      }}
    >
      {/* Advanced Editor Button */}
      {isAdvancedEditorButtonEnabled && userPermissions?.isOwner && !miniRightSidebar && !editorOptionShown && (
        <div className={styles.advancedEditorButton}>
          <CustomButton
            label={t('clip_organizer.sidebar_advanced_editor_button')}
            variant="secondary"
            onClick={() => onShowAdvancedEditorDialog()}
            className={styles.sidebarButton}
          />
        </div>
      )}
      {!miniRightSidebar && !editorOptionShown && collaboratingUsers.length ? (
        <div className={styles.headerUserList} ref={userListRef}>
          <UsersListPreview users={collaboratingUsers} parentRef={userListRef} />
        </div>
      ) : null}
      {sidebarState === SIDEBAR_STATE_LIST.STORYBOARD.TYPE && (
        <div className={styles.narrativeWrapper}>
          {currentClipData && (
            <div className={styles.scriptWrapper}>
              <div className={styles.headerContainer}>
                <NameEditComponent
                  size="small"
                  name={currentClipData?.name}
                  handleSaveName={handleNameChange}
                  isEditIcon={true}
                  disableEditing={!isSceneAllowed}
                  changeTextEdit={changeTextEdit} />
              </div>
              <TextComponent
                id={currentClipData?.id}
                key={'narrative'}
                title={t('storyboard.editor.narrative_name')}
                text={currentClipData?.narrative}
                className={styles.narrativeCard}
                placeholder={t('storyboard.editor.narrative_placeholder')}
                linesWithCollaps={4}
                onSaveChanges={handleNarrativeChange}
                changeTextEdit={changeTextEdit}
                disableEditing={!isSceneAllowed}
              />
              <TextComponent
                id={currentClipData?.id}
                key={'script'}
                title={t('storyboard.editor.script_name')}
                text={currentClipData?.script}
                className={styles.narrativeCard}
                placeholder={t('storyboard.editor.script_placeholder')}
                linesWithCollaps={4}
                onSaveChanges={handleScriptChange}
                changeTextEdit={changeTextEdit}
                disableEditing={!isSceneAllowed}
              />
            </div>
          )}

          {!miniRightSidebar && (
            <div className={styles.footer}>
              <CustomButton
                label={t('general.editor.save_changes')}
                variant="primary"
                onClick={handleSaveClip}
                disabled={saving || !hasChanged || isSceneProcessing || isTextEdit}
                loading={saving}
                loadingSpinner={true}
                className={styles.sidebarButton}
              />
            </div>
          )}
        </div>
      )}
      {sidebarState === SIDEBAR_STATE_LIST.EDIT.TYPE && (
        <EditorSidebarActions
          video={video}
          logo={logo}
          isDraft={isDraft}
          currentVideoPart={currentVideoPart}
          sceneIndex={sceneIndex}
          activeScene={activeScene}
          rename={rename}
          handleRename={handleRename}
          handleSaveClip={handleSaveClip}
          saving={saving}
          isSceneProcessing={isSceneProcessing}
          cancelEditing={cancelEditing}
          handleUndo={handleUndo}
          handleRedo={handleRedo}
          controlsDisabled={controlsDisabled}
          trimmingMode={trimmingMode}
          handleTrimVideo={handleTrimVideo}
          init={init}
          mini={miniRightSidebar}
          setMiniSidebar={setMiniRightSidebar}
          hasChanged={hasChanged}
          handleStartTrimming={handleStartTrimming}
          switchingTrimming={switchingTrimming}
          isTransitionExists={isTransitionExists}
          isTransitionAllowed={isTransitionAllowed}
          activeTransitionRightMedia={activeTransitionRightMedia}
          onTransitionChanged={onTransitionChanged}
          currentTransitionType={currentTransitionType}
          handleSaveTextPanel={handleSaveTextPanel}
          handleIntroOutro={handleIntroOutro}
          onBackgroundMusicChange={onBackgroundMusicChange}
          handleSpeedUpChange={handleSpeedUpChange}
          isVideoHasHls={isVideoHasHls}
          handleSplit={handleSplit}
          userPermissions={userPermissions}
          handleLogoChange={handleLogoChange}
          handleZoomPointsChange={handleZoomPointsChange}
          addTransitionToScene={addTransitionToScene}
          isRecording={isRecording}
          handleEditStoryboard={handleEditStoryboard}
          handleCompleteAssignment={handleCompleteAssignment}
          isCurrentAssignedScenesCompleted={isCurrentAssignedScenesCompleted}
          isCurrentAssignedScenesRecorded={isCurrentAssignedScenesRecorded}
          onEnhanceScene={onEnhanceScene}
          onRemoveEnhanceScene={onRemoveEnhanceScene}
        />
      )}
      {sidebarState === SIDEBAR_STATE_LIST.CHAT.TYPE && (
        <CommentsTab
          noCommentsMessage={!isCommentsLoading && comments?.length === 0 && t('storyboard.editor.no_comments_message')}
          updateComments={loadComments}
          isCommentsLoading={isCommentsLoading}
          storyId={storyId}
          comments={comments}
          commentInit={commentInit}
          newComment={newComment}
          setNewComment={setNewComment}
          highlightFirst={false}
        />
      )}
    </RightSidebarContainer>
  );
};

export default EditorRightSidebar;
